/**
 * Unified logo bar — frosted glass container with teal glow.
 *
 * Replaces: LogoStripInline, LogoGridResponsive, LogosGridSimple,
 *           LogosMarquee, LogosStripSmall, LogosGridDark
 *
 * Accepts various prop shapes for backward compatibility with all
 * former block types. All render the same visual treatment.
 *
 * Logo paths are normalized to the -navy.svg variant (designed at 32px
 * height) for consistent sizing. Color is applied via CSS filter.
 */

/**
 * Normalize logo paths to the -navy.svg variant for consistent sizing.
 * The navy SVGs are all designed at height=32 with tight bounding boxes,
 * while slate/blue variants have smaller viewBoxes (16-28px) that scale
 * poorly at h-8.
 */
function normalizeLogoPath(src: string): string {
  // Map known color variants to navy: -slate, -blue, -dark, -gray
  // Also map uncolored base variants that have navy equivalents
  const navyMap: Record<string, string> = {
    "/images/pricesmart-slate.svg": "/images/pricesmart-navy.svg",
    "/images/pricesmart-blue.svg": "/images/pricesmart-navy.svg",
    "/images/pricesmart-gray.svg": "/images/pricesmart-navy.svg",
    "/images/pricesmart.svg": "/images/pricesmart-navy.svg",
    "/images/pricesmart-purple.svg": "/images/pricesmart-navy.svg",
    "/images/logo-blue-pricesmart.svg": "/images/pricesmart-navy.svg",
    "/images/qualtrics.svg": "/images/qualtrics-navy.svg",
    "/images/logo-blue-ramp.svg": "/images/ramp-navy.svg",
    "/images/ramp-slate.svg": "/images/ramp-navy.svg",
    "/images/ramp-blue.svg": "/images/ramp-navy.svg",
    "/images/ramp-gray.svg": "/images/ramp-navy.svg",
    "/images/ramp-purple.svg": "/images/ramp-navy.svg",
    "/images/ramp.svg": "/images/ramp-navy.svg",
    "/images/logo-blue-instacart.svg": "/images/instacart-navy.svg",
    "/images/instacart-slate.svg": "/images/instacart-navy.svg",
    "/images/instacart-blue.svg": "/images/instacart-navy.svg",
    "/images/instacart-gray.svg": "/images/instacart-navy.svg",
    "/images/instacart-purple.svg": "/images/instacart-navy.svg",
    "/images/instacart.svg": "/images/instacart-navy.svg",
    "/images/zscaler-slate.svg": "/images/zscaler-navy.svg",
    "/images/zscaler-blue.svg": "/images/zscaler-navy.svg",
    "/images/zscaler-gray.svg": "/images/zscaler-navy.svg",
    "/images/zscaler-purple.svg": "/images/zscaler-navy.svg",
    "/images/zscaler.svg": "/images/zscaler-navy.svg",
    "/images/logo-blue-zscaler.svg": "/images/zscaler-navy.svg",
    "/images/do-slate.svg": "/images/do-navy.svg",
    "/images/do-blue.svg": "/images/do-navy.svg",
    "/images/do-gray.svg": "/images/do-navy.svg",
    "/images/do-purple.svg": "/images/do-navy.svg",
    "/images/digitalocean.svg": "/images/do-navy.svg",
    "/images/logo-blue-do.svg": "/images/do-navy.svg",
    "/images/doordash.svg": "/images/doordash-navy.svg",
    "/images/doordash-dark.svg": "/images/doordash-navy.svg",
    "/images/logo-blue-brex.svg": "/images/brex-navy.svg",
    "/images/brex-slate.svg": "/images/brex-navy.svg",
    "/images/brex-blue.svg": "/images/brex-navy.svg",
    "/images/brex-gray.svg": "/images/brex-navy.svg",
    "/images/brex-purple.svg": "/images/brex-navy.svg",
    "/images/brex.svg": "/images/brex-navy.svg",
    "/images/logo-blue-klaviyo.svg": "/images/klaviyo-navy.svg",
    "/images/klaviyo-slate.svg": "/images/klaviyo-navy.svg",
    "/images/klaviyo-blue.svg": "/images/klaviyo-navy.svg",
    "/images/klaviyo-dark.svg": "/images/klaviyo-navy.svg",
    "/images/klaviyo-gray.svg": "/images/klaviyo-navy.svg",
    "/images/klaviyo-purple.svg": "/images/klaviyo-navy.svg",
    "/images/klaviyo.svg": "/images/klaviyo-navy.svg",
    "/images/aveva.svg": "/images/aveva-navy.svg",
    "/images/aveva-dark.svg": "/images/aveva-navy.svg",
    "/images/Aveva_Dark.svg": "/images/aveva-navy.svg",
  };
  return navyMap[src] ?? src;
}

const AVEVA_LOGO = "/images/aveva-navy.svg";

const CUSTOMER_LOGO_PATHS = new Set([
  "/images/qualtrics-navy.svg",
  "/images/qualtrics.svg",
  "/images/ramp-navy.svg",
  "/images/logo-blue-ramp.svg",
  "/images/ramp-slate.svg",
  "/images/ramp-blue.svg",
  "/images/ramp-gray.svg",
  "/images/ramp-purple.svg",
  "/images/ramp.svg",
  "/images/Ramp.png",
  "/images/instacart-navy.svg",
  "/images/logo-blue-instacart.svg",
  "/images/instacart-slate.svg",
  "/images/instacart-blue.svg",
  "/images/instacart-gray.svg",
  "/images/instacart-purple.svg",
  "/images/instacart.svg",
  "/images/Instacart_Logo_AllBlack.png",
  "/images/zscaler-navy.svg",
  "/images/zscaler-slate.svg",
  "/images/zscaler-blue.svg",
  "/images/zscaler-gray.svg",
  "/images/zscaler-purple.svg",
  "/images/zscaler.svg",
  "/images/logo-blue-zscaler.svg",
  "/images/do-navy.svg",
  "/images/logo-blue-do.svg",
  "/images/do-slate.svg",
  "/images/do-blue.svg",
  "/images/do-gray.svg",
  "/images/do-purple.svg",
  "/images/digitalocean.svg",
  "/images/DigitalOcean.png",
  "/images/doordash-navy.svg",
  "/images/doordash.svg",
  "/images/doordash-dark.svg",
  "/images/brex-navy.svg",
  "/images/logo-blue-brex.svg",
  "/images/brex-slate.svg",
  "/images/brex-blue.svg",
  "/images/brex-gray.svg",
  "/images/brex-purple.svg",
  "/images/brex.svg",
  "/images/klaviyo-navy.svg",
  "/images/logo-blue-klaviyo.svg",
  "/images/klaviyo-slate.svg",
  "/images/klaviyo-blue.svg",
  "/images/klaviyo-gray.svg",
  "/images/klaviyo-purple.svg",
  "/images/klaviyo.svg",
  "/images/aveva-navy.svg",
  "/images/aveva.svg",
  "/images/aveva-dark.svg",
  "/images/Aveva_Dark.svg",
]);

function isAvevaLogo(src: string): boolean {
  return src.toLowerCase().includes("aveva");
}

function isCustomerLogo(src: string): boolean {
  return CUSTOMER_LOGO_PATHS.has(src) || CUSTOMER_LOGO_PATHS.has(normalizeLogoPath(src));
}

function withCustomerLogoAdditions(srcs: string[]): string[] {
  const customerLogoCount = new Set(
    srcs.filter(isCustomerLogo).map(normalizeLogoPath)
  ).size;
  if (customerLogoCount < 2) return srcs;

  const insertAfter = [
    "/images/instacart-navy.svg",
    "/images/ramp-navy.svg",
    "/images/brex-navy.svg",
  ]
    .map((target) => srcs.findIndex((src) => normalizeLogoPath(src) === target))
    .find((index) => index !== -1);

  const next = [...srcs];
  const insertAt = insertAfter === undefined ? next.length : insertAfter + 1;

  if (!next.some(isAvevaLogo)) {
    next.splice(insertAt, 0, AVEVA_LOGO);
  }

  return next;
}

function splitBalancedRows(srcs: string[], rowCount: number): string[][] {
  const baseSize = Math.floor(srcs.length / rowCount);
  const extra = srcs.length % rowCount;
  const rows: string[][] = [];
  let start = 0;

  for (let rowIndex = 0; rowIndex < rowCount; rowIndex += 1) {
    const rowSize = baseSize + (rowIndex < extra ? 1 : 0);
    rows.push(srcs.slice(start, start + rowSize));
    start += rowSize;
  }

  return rows.filter(
    (row) => row.length > 0
  );
}

function splitDesktopLogoRows(srcs: string[]): string[][] {
  if (srcs.length <= 10) return [srcs];
  return splitBalancedRows(srcs, Math.ceil(srcs.length / 8));
}

function splitLogoRows(srcs: string[]): string[][] {
  if (srcs.length <= 4) return [srcs];

  const rowCount = srcs.length > 10
    ? Math.ceil(srcs.length / 3)
    : srcs.length > 8
      ? 3
      : 2;
  return splitBalancedRows(srcs, rowCount);
}

export function normalizeCustomerLogoImages(srcs: string[]): string[] {
  return withCustomerLogoAdditions(srcs).map(normalizeLogoPath);
}

/** CSS filter that tints any image to teal-dark (#00343a) */
const TEAL_FILTER = "brightness(0) invert(1) sepia(1) hue-rotate(155deg) saturate(5) brightness(0.25)";
/** CSS filter that tints any image to pure white for dark backgrounds */
export const WHITE_FILTER = "brightness(0) invert(1)";

export interface LogoBarProps {
  heading?: string;
  images?: string[];
  logos?: string[];
  variant?: "light" | "dark" | string;
  /**
   * When true, the dark variant skips its container background so the bar
   * floats over a parent-provided shared background image.
   */
  transparent?: boolean;
  /** Drop the default top margin on the bar container (e.g. when the parent overlaps it). */
  flushTop?: boolean;
  /** Override the light-variant drop-shadow color (defaults to the teal glow). */
  shadowColor?: string;
  /** Override the light-variant drop-shadow blur radius in px (defaults to 30). */
  shadowBlur?: number;
  /** Glassmorphism: more translucent + stronger backdrop blur so the background shows through. */
  glass?: boolean;
  // Accepted but ignored (backward compat)
  background?: string;
  image_mobile?: string;
  image_desktop?: string;
  logos_mobile?: string;
  logos_desktop?: string;
}

export function LogoBar({
  heading,
  images,
  logos,
  variant,
  transparent,
  flushTop,
  shadowColor,
  shadowBlur,
  glass,
}: LogoBarProps) {
  const imageList = normalizeCustomerLogoImages(images || logos || []);
  if (imageList.length === 0) return null;
  const desktopLogoRows = splitDesktopLogoRows(imageList);
  const mobileLogoRows = splitLogoRows(imageList);

  const isDark = variant === "dark";
  const topMargin = flushTop ? "" : "mt-5";
  // Light-variant surface: default frosted white, or a more translucent glass
  // treatment (lower opacity + stronger blur) when `glass` is set. Blur is a
  // backdrop-filter, so it only affects the background — the logo <img>s stay crisp.
  const lightSurface = glass ? "bg-white" : "bg-white/80 backdrop-blur-sm";
  // The inner rounded container always has a solid bg in dark mode, even when
  // the outer wrapper is transparent (e.g. floating over a shared page bg).
  const containerClass = isDark
    ? `items-center px-8 py-5 ${topMargin} justify-between gap-x-5 rounded-20 bg-[#141414]`
    : `items-center px-8 py-5 ${topMargin} justify-between gap-x-5 rounded-20 ${lightSurface}`;
  const multiRowContainerClass = isDark
    ? `flex-col px-8 py-5 ${topMargin} gap-y-4 rounded-20 bg-[#141414]`
    : `flex-col px-8 py-5 ${topMargin} gap-y-4 rounded-20 ${lightSurface}`;
  const mobileContainerClass = isDark
    ? `flex-col items-center px-4 py-6 ${topMargin} gap-y-4 rounded-20 bg-[#141414] sm:px-8 md:px-10 md:py-7`
    : `flex-col items-center px-4 py-6 ${topMargin} gap-y-4 rounded-20 ${lightSurface} sm:px-8 md:px-10 md:py-7`;
  const containerStyle = isDark
    ? undefined
    : { boxShadow: `0 4px ${shadowBlur ?? 30}px ${shadowColor ?? "rgba(138, 241, 217, 0.4)"}` };
  const logoFilter = isDark ? WHITE_FILTER : TEAL_FILTER;
  const desktopLogoClass =
    imageList.length > 10
      ? "h-[22px] w-auto max-w-[118px] object-contain xl:h-[26px] xl:max-w-[136px]"
      : imageList.length >= 9
      // 24/122 at lg, not 26/132: nine logos at 26px measure ~930px against the
      // ~897px the card offers at a 1040px viewport (minus a 15px classic
      // scrollbar), and `<img>` flex items have `min-width: auto`, so they do
      // not shrink — the row silently overhangs the card's right edge instead.
      // Height and max-width step down together so every logo keeps its
      // proportions and their relative sizes are unchanged.
      ? "h-[24px] w-auto max-w-[122px] object-contain xl:h-[30px] xl:max-w-[152px]"
      : "h-[30px] w-auto max-w-[152px] object-contain";
  const desktopContainerClass =
    desktopLogoRows.length > 1 ? multiRowContainerClass : containerClass;

  return (
    <div className="relative z-10 px-4 md:px-8">
      <div className="relative max-w-screen-xl mx-auto">
        {heading && (
          <div className="pt-5">
            <h2 className={`font-body font-semibold uppercase text-11 tracking-widest ${isDark ? "text-white/60" : "text-gray-60"}`}>
              {heading}
            </h2>
          </div>
        )}
        {/* Desktop: one row for standard sets, balanced rows for larger legacy sets */}
        <div className={`hidden lg:flex ${desktopContainerClass}`} style={containerStyle}>
          {desktopLogoRows.map((row, rowIndex) => (
            <div
              key={rowIndex}
              className="flex w-full items-center justify-between gap-x-4 xl:gap-x-5"
            >
              {row.map((img, i) => (
                <img
                  key={`${rowIndex}-${i}`}
                  src={img}
                  loading="lazy"
                  sizes="200px"
                  className={desktopLogoClass}
                  style={{ filter: logoFilter }}
                  alt=""
                />
              ))}
            </div>
          ))}
        </div>
        {/* Tablet/mobile: centered rows with the full customer set */}
        <div className={`flex lg:hidden ${mobileContainerClass}`} style={containerStyle}>
          {mobileLogoRows.map((row, rowIndex) => (
            <div
              key={rowIndex}
              className="flex w-full items-center justify-between gap-x-4 sm:gap-x-8 md:gap-x-12"
            >
              {row.map((img, i) => (
                <img
                  key={`${rowIndex}-${i}`}
                  src={img}
                  loading="lazy"
                  sizes="160px"
                  className="h-[14px] w-auto max-w-[22vw] shrink-0 object-contain xs:h-[18px] sm:h-[22px] md:h-[26px]"
                  style={{ filter: logoFilter }}
                  alt=""
                />
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
